<template>
  <article
    class="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-sand-200 bg-white shadow-warm-sm transition-all duration-300 focus-within:ring-2 focus-within:ring-primary-500 focus-within:ring-offset-2 hover:-translate-y-1 hover:border-sand-300 hover:shadow-warm-lg"
  >
    <!-- Image -->
    <div class="relative aspect-[4/3] overflow-hidden bg-sand-100">
      <img
        v-if="hotel.image && !imageFailed"
        :src="hotel.image"
        :alt="`${hotel.name}${hotel.location ? `, ${hotel.location}` : ''}`"
        loading="lazy"
        decoding="async"
        class="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        @error="imageFailed = true"
      />
      <div
        v-else
        class="flex h-full w-full items-center justify-center bg-linear-to-br from-sand-100 to-sand-200 text-ink-300"
      >
        <i class="bi bi-building text-5xl"></i>
      </div>

      <!-- Soft shade so the white badges stay readable on bright photos -->
      <div
        class="pointer-events-none absolute inset-0 bg-linear-to-t from-ink-900/45 via-transparent to-ink-900/15"
      ></div>

      <span
        class="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1 text-xs font-semibold text-ink-800 shadow-warm-sm backdrop-blur"
      >
        <i :class="['bi', typeIcon, 'text-primary-600']"></i>
        {{ hotel.type || "Hotel" }}
      </span>

      <!-- Sits above the card's stretched link so it stays clickable -->
      <button
        type="button"
        class="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/95 shadow-warm-sm backdrop-blur transition-transform duration-200 hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-60"
        :aria-label="
          isFavourite ? 'Remove from favourites' : 'Add to favourites'
        "
        :aria-pressed="isFavourite"
        :disabled="favouritePending"
        @click="$emit('toggle-favourite', hotel.id)"
      >
        <span
          v-if="favouritePending"
          class="spinner spinner-sm"
          role="status"
          aria-label="Loading"
        ></span>
        <i
          v-else
          :class="[
            'bi text-base',
            isFavourite
              ? 'bi-heart-fill text-red-500'
              : 'bi-heart text-ink-700',
          ]"
        ></i>
      </button>

      <span
        v-if="hotel.distance"
        class="absolute bottom-3 left-3 inline-flex items-center gap-1 rounded-full bg-ink-900/60 px-2.5 py-1 text-xs font-medium text-white backdrop-blur"
      >
        <i class="bi bi-geo-alt-fill"></i>
        {{ hotel.distance }}
      </span>

      <span
        v-if="photoCount > 1"
        class="absolute bottom-3 right-3 inline-flex items-center gap-1 rounded-full bg-ink-900/60 px-2.5 py-1 text-xs font-medium text-white backdrop-blur"
      >
        <i class="bi bi-images"></i>
        {{ photoCount }}
      </span>
    </div>

    <!-- Details -->
    <div class="flex flex-1 flex-col p-5">
      <div class="flex items-start justify-between gap-3">
        <h3
          class="line-clamp-1 font-display text-lg font-semibold leading-snug text-ink-900"
        >
          <!-- Stretched link: its ::after covers the whole card -->
          <router-link
            :to="{ name: 'hotel-details', params: { id: hotel.id } }"
            class="after:absolute after:inset-0 after:content-[''] focus:outline-none"
          >
            {{ hotel.name }}
          </router-link>
        </h3>
        <span
          v-if="hotel.rating > 0"
          class="inline-flex shrink-0 items-center gap-1 rounded-md bg-gold-50 px-2 py-0.5 text-sm font-semibold text-ink-800"
          :aria-label="`Rated ${hotel.rating} out of 5`"
        >
          <i class="bi bi-star-fill text-xs text-gold-500"></i>
          {{ formattedRating }}
        </span>
      </div>

      <p
        v-if="hotel.location"
        class="mt-1 line-clamp-1 flex items-center gap-1.5 text-sm text-ink-500"
      >
        <i class="bi bi-geo-alt text-primary-500"></i>
        {{ hotel.location }}
      </p>

      <p
        v-if="hotel.description"
        class="mt-3 line-clamp-2 text-sm leading-relaxed text-ink-600"
      >
        {{ hotel.description }}
      </p>

      <!-- Pushes the footer to the bottom so cards in a row line up -->
      <div class="min-h-4 flex-1"></div>

      <div
        class="flex items-center justify-between border-t border-sand-200 pt-4 text-sm font-semibold text-primary-600"
        aria-hidden="true"
      >
        View details
        <span
          class="flex h-8 w-8 items-center justify-center rounded-full bg-primary-50 transition-all duration-300 group-hover:translate-x-1 group-hover:bg-primary-500 group-hover:text-white"
        >
          <i class="bi bi-arrow-right"></i>
        </span>
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import type { HotelDisplay } from "@/stores/hotel.store";

const props = defineProps<{
  hotel: HotelDisplay;
  isFavourite?: boolean;
  favouritePending?: boolean;
}>();

defineEmits<{
  (e: "toggle-favourite", hotelId: string): void;
}>();

const imageFailed = ref(false);
watch(
  () => props.hotel.image,
  () => (imageFailed.value = false)
);

const typeIcon = computed(() =>
  /apartment|short/i.test(props.hotel.type || "")
    ? "bi-house-door"
    : "bi-building"
);

const photoCount = computed(() => props.hotel.imageUrls?.length || 0);

const formattedRating = computed(() => props.hotel.rating.toFixed(1));
</script>
